import { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

function formatMoney(n) {
  return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function CumulativeSavingsChart({ savings }) {
  const data = useMemo(() => {
    const trend = savings?.trend || [];
    let running = 0;
    return trend.map((row) => {
      running += row.savings ?? 0;
      return {
        name: row.period_label,
        cumulative: running,
        periodSavings: row.savings ?? 0,
      };
    });
  }, [savings]);

  return (
    <Card data-testid="bv-cumulative-savings">
      <CardHeader>
        <CardTitle>Cumulative savings</CardTitle>
        <CardDescription>Running total of manual value minus LLM cost across the range</CardDescription>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No trend data for this range.</p>
        ) : (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data}>
                <defs>
                  <linearGradient id="bvSavingsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`} />
                <Tooltip
                  formatter={(v, name) => [
                    formatMoney(v),
                    name === 'cumulative' ? 'Cumulative savings' : 'Period savings',
                  ]}
                />
                <Area
                  type="monotone"
                  dataKey="cumulative"
                  stroke="#10b981"
                  strokeWidth={2.5}
                  fill="url(#bvSavingsGrad)"
                  name="cumulative"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
        <p className="mt-2 text-xs text-muted-foreground">
          Each resolved ticket adds manual baseline savings while LLM cost stays low — the line compounds as volume grows.
        </p>
      </CardContent>
    </Card>
  );
}
